import { VStack, cssVar, Caution, HStack, Icon, Flex, Text } from '@cad-arc';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { ConsolidatedLocationView, EventPanelMetadata } from '@mark43/cad-api';
import { SectionTitle } from '../../section-title';
import { useEventLocationCautionsQuery } from '../../../event-panel/event-state/use-event-location-cautions-query';
import { useEventMetadataStore } from '../../../event-panel/event-panel-form/event-metadata';
import { useEventPriority, useRiskLevel } from '../hooks';
import { ExpandableContent } from '../../../common/components/expandable-content';
import { AssociatedPersonsSection } from '../../../event-panel/tabs/cautions/components/sections/associated-persons-section';
import { useEventAssociatedPersonsQuery } from '../../../event-panel/event-state/use-event-associated-persons-query';
import { GoogleMapsRouteButton } from '../../google-maps-route-button';
import { FrEventNearbyCautions } from '../../fr-event-nearby-cautions';
import { FrEventLocationSelectValueDisplay } from '../../fr-event-location-select-value-display';
import { FrEventPreviewMap } from './fr-event-preview-map';

export function LocationSection() {
    const { eventId } = useEventMetadataStore()?.state?.metadata ?? ({} as EventPanelMetadata);
    const { mapRouteEnabled } = useEnabledFeatures();
    const eventPriority = useEventPriority();
    const riskLevel = useRiskLevel();
    const form = useFormContext();
    const { data: cautions, isLoading: areCautionsLoading } = useEventLocationCautionsQuery({
        eventId,
    });
    const {
        data: associatedPersons,
        isLoading: areAssociatedPersonsLoading,
    } = useEventAssociatedPersonsQuery({ eventId });

    const eventLocation: ConsolidatedLocationView = useWatch({
        control: form.control,
        name: 'location',
    });

    return (
        <VStack
            w="100%"
            justifyContent="flex-start"
            alignItems="flex-start"
            margin={0}
            gap="5px"
            padding={`${cssVar('arc.space.6')} ${cssVar('arc.space.6')} 0`}
            data-testid="fr-location-section"
        >
            <HStack justifyContent="space-between" width="100%" m={0}>
                <SectionTitle title="Location" />
                <HStack>
                    {eventPriority?.label && (
                        <Caution priority={eventPriority.level} data-testid="event-priority-label">
                            {eventPriority.label}
                        </Caution>
                    )}
                    {riskLevel?.label && (
                        <Caution priority={riskLevel.level} data-testid="risk-level-label">
                            {riskLevel.label}
                        </Caution>
                    )}
                </HStack>
            </HStack>
            {!eventLocation?.areCoordinatesVerified ? (
                <Flex w="100%" h="202px" alignItems="center" justifyContent="center">
                    No location coordinates found.
                </Flex>
            ) : (
                <FrEventPreviewMap
                    eventLocation={eventLocation}
                    eventPriority={eventPriority?.label ?? ''}
                />
            )}
            <VStack w="100%" alignItems="flex-start">
                <FrEventLocationSelectValueDisplay
                    cautions={cautions}
                    areCautionsLoading={areCautionsLoading}
                />
                {mapRouteEnabled && <GoogleMapsRouteButton />}
            </VStack>
            <FrEventNearbyCautions areCautionsLoading={areCautionsLoading} cautions={cautions} />
            {!!associatedPersons?.cautionsTabAssociatedPeople?.length && (
                <ExpandableContent
                    title={
                        <Text
                            color="info"
                            fontWeight="bold"
                            style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                        >
                            <Icon size="sm" icon="Alert" />
                            {`Associated Persons (${associatedPersons.personsCount})`}
                        </Text>
                    }
                >
                    <AssociatedPersonsSection
                        data={associatedPersons}
                        isLoading={areAssociatedPersonsLoading}
                    />
                </ExpandableContent>
            )}
        </VStack>
    );
}
